import {
  createBaseThemeOptions,
  pageTheme as defaultPageThemes,
  PageTheme,
  palettes,
  createUnifiedTheme,
  defaultComponentThemes,
  UnifiedTheme,
  UnifiedThemeOptions,
} from '@backstage/theme';
import {
  UZUM_FONT,
  UzumMode,
  alpha,
  uzumColors as c,
  uzumSemantic,
  uzumWarning,
} from './uzumTokens';

/**
 * Uzum brand themes, light and dark, both built from the design-system tokens
 * in `uzumTokens.ts`. The brand face is TT Uzum (a licensed TypeType typeface we
 * cannot bundle); it is named first so anyone who has it installed gets it, with
 * Montserrat — also used on uzum.com — and Roboto as fallbacks.
 */

// Backstage page headers each carry an accent gradient; give every one the
// design system's "Success Gradient" (Brand 700 → Black 950) so the whole app
// reads as one brand rather than the stock rainbow. It is the same in both modes.
const brandedPageThemes: Record<string, PageTheme> = {};
Object.keys(defaultPageThemes).forEach(key => {
  brandedPageThemes[key] = {
    ...defaultPageThemes[key],
    colors: [c.brand700, c.black950],
    shape: defaultPageThemes[key].shape,
    backgroundImage: `linear-gradient(110deg, ${c.brand700} 0%, ${c.brand900} 45%, ${c.black950} 100%)`,
    fontColor: c.black0,
  };
});

type Style = Record<string, unknown>;
type StyleProps = { theme: unknown };
type Slot = Style | ((props: StyleProps) => Style);
type Overrides = Record<string, { styleOverrides: Record<string, Slot> }>;

const resolve = (slot: Slot | undefined, props: StyleProps): Style =>
  typeof slot === 'function' ? slot(props) : slot ?? {};

/**
 * `createUnifiedTheme` merges `components` into Backstage's own
 * `defaultComponentThemes` one level deep: a key we set replaces the default
 * for that whole component, and a slot we set (`root`, `head`, …) drops the
 * default's rules for it. Compose instead — defaults first, ours on top — so an
 * override only ever adds to what Backstage does.
 */
function withDefaults(ours: Overrides): UnifiedThemeOptions['components'] {
  const defaults = (defaultComponentThemes ?? {}) as Record<
    string,
    { styleOverrides?: Record<string, Slot> }
  >;
  const merged: Record<string, unknown> = {};
  for (const [name, { styleOverrides }] of Object.entries(ours)) {
    const base = defaults[name]?.styleOverrides ?? {};
    const slots: Record<string, Slot> = { ...base };
    for (const [slot, style] of Object.entries(styleOverrides)) {
      const def = base[slot];
      slots[slot] = def
        ? (props: StyleProps) => ({
            ...resolve(def, props),
            ...resolve(style, props),
          })
        : style;
    }
    merged[name] = { ...defaults[name], styleOverrides: slots };
  }
  return merged as UnifiedThemeOptions['components'];
}

/**
 * Component overrides. The catalog page's table and search are still
 * Material UI v4 and are restyled here through the class names Backstage gives
 * its styles — the page itself is stock. Its filters, the nav and the frame
 * are Backstage UI (modules/catalogFilters, modules/nav) and are not here. Keys under `&.Mui-<state>` alone are
 * hoisted by Backstage into a `<state>` class key, which only Mui* components
 * have; pair them with another selector (`&.Mui-selected, &.Mui-selected:hover`)
 * to keep them nested.
 */
function uzumComponents(mode: UzumMode): Overrides {
  const s = uzumSemantic[mode];
  const dark = mode === 'dark';
  // "Container White": a field sitting on the grey canvas.
  const field = dark ? s.container.grey : c.black0;
  // "Container Grey": a field sitting on a white sheet.
  const fieldOnSheet = dark ? alpha(c.black0, 0.08) : c.blueBlack50;
  // Tags: "Container Accent" is too dense under small text in dark, so the
  // chip takes a lighter fill and Brand 300 ink there.
  const chipBg = dark ? alpha(c.brand400, 0.24) : s.container.accent;
  const chipInk = dark ? c.brand300 : s.text.accent;

  return {
    BackstageHeader: {
      styleOverrides: {
        header: { boxShadow: 'unset' },
        title: { color: c.black0, fontWeight: 800 },
        subtitle: { color: alpha(c.black0, 0.8) },
        type: { color: alpha(c.black0, 0.8) },
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        defaultTab: {
          fontSize: 'inherit',
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 10, textTransform: 'none', fontWeight: 700 },
        contained: { boxShadow: 'none' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: chipBg,
          color: chipInk,
        },
        // Table tags are `variant="outlined"`: fill them like any other chip.
        outlined: {
          border: 'none',
          backgroundColor: chipBg,
        },
        label: { fontWeight: 600 },
      },
    },
    // Links take `color="primary"`, but the sheets give text links their own
    // token — in dark, Brand 600 on Black 900 is too faint to read.
    MuiLink: {
      styleOverrides: {
        root: {
          '&.MuiTypography-colorPrimary': { color: s.text.link },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: s.action.primary,
          height: 3,
          borderRadius: 3,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 16 },
      },
    },
    MuiTypography: {
      styleOverrides: {
        button: { textTransform: 'none' },
      },
    },

    // Tables: hairlines instead of zebra stripes, and words that break at
    // their hyphens rather than mid-word (`admin-panel-backen|d`).
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(odd)': { backgroundColor: 'transparent' },
          '&:not(.MuiTableRow-head):hover': {
            backgroundColor: s.container.pressed,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          wordBreak: 'normal',
          overflowWrap: 'break-word',
          lineHeight: 1.4,
          borderBottom: `1px solid ${s.border.subtle}`,
        },
        sizeSmall: { padding: '12px 10px' },
        head: { color: s.text.secondary },
      },
    },
    BackstageTable: {
      styleOverrides: {
        root: {
          '& > .MuiPaper-root': { borderRadius: 20, boxShadow: 'none' },
          // material-table sets column widths inline, so the name column ends
          // up ~140 px wide; keep the entity name whole and let the empty and
          // free-text columns give way instead.
          '& tbody td:first-child a': { whiteSpace: 'nowrap' },
        },
      },
    },
    BackstageTableHeader: {
      styleOverrides: {
        header: {
          padding: '10px 10px',
          textTransform: 'none',
          fontSize: 13,
          fontWeight: 600,
          color: s.text.secondary,
          borderTop: `1px solid ${s.border.subtle}`,
          borderBottom: `1px solid ${s.border.subtle}`,
        },
      },
    },
    PluginCatalogTableToolbar: {
      styleOverrides: {
        root: { padding: '20px 24px', alignItems: 'center' },
        text: { fontSize: 20, fontWeight: 700 },
      },
    },
    CatalogReactEntitySearchBar: {
      styleOverrides: {
        input: {
          minWidth: 280,
          height: 44,
          padding: '0 12px',
          borderRadius: 12,
          backgroundColor: fieldOnSheet,
          '&:before, &:after': { display: 'none' },
        },
      },
    },

    // core-components' Select — search filters, the language toggle in
    // Settings: a plain field on the canvas.
    BackstageSelectInputBase: {
      styleOverrides: {
        input: {
          borderRadius: 12,
          border: '1px solid transparent',
          backgroundColor: field,
          padding: '11px 40px 11px 14px',
          '&:focus': { backgroundColor: field, borderRadius: 12 },
        },
      },
    },
  };
}

function createUzumTheme(mode: UzumMode): UnifiedTheme {
  const s = uzumSemantic[mode];
  const dark = mode === 'dark';
  const base = dark ? palettes.dark : palettes.light;

  return createUnifiedTheme({
    ...createBaseThemeOptions({
      palette: {
        ...base,
        primary: {
          main: s.action.primary,
          light: dark ? c.brand400 : c.brand500,
          dark: s.action.primaryPressed,
          contrastText: s.action.onPrimary,
        },
        secondary: {
          main: dark ? c.brand400 : c.brand600,
          light: c.brand300,
          dark: c.brand800,
        },
        grey: {
          50: c.blueBlack50,
          100: c.blueBlack100,
          200: c.blueBlack200,
          300: c.blueBlack300,
          400: '#8F93A0',
          500: c.blueBlack600,
          600: c.blueBlack700,
          700: c.blueBlack900,
          800: c.black900,
          900: c.black950,
        },
        error: {
          main: s.text.error,
          light: c.red400,
          dark: c.red600,
        },
        warning: uzumWarning,
        success: {
          main: s.text.success,
          light: c.green400,
          dark: c.green600,
        },
        info: {
          main: s.text.link,
          light: c.brand300,
          dark: c.brand800,
        },
        text: {
          primary: s.text.primary,
          secondary: s.text.secondary,
          disabled: s.text.disabled,
        },
        background: {
          default: s.background.main,
          paper: s.background.sheet,
        },
        divider: s.border.strong,
        link: s.text.link,
        linkHover: dark ? c.brand300 : c.brand800,
      },
    }),
    fontFamily: UZUM_FONT,
    typography: {
      fontFamily: UZUM_FONT,
      htmlFontSize: 16,
      h1: { fontSize: 48, fontWeight: 800, marginBottom: 10 },
      h2: { fontSize: 36, fontWeight: 800, marginBottom: 8 },
      h3: { fontSize: 30, fontWeight: 700, marginBottom: 6 },
      h4: { fontSize: 26, fontWeight: 700, marginBottom: 6 },
      h5: { fontSize: 22, fontWeight: 700, marginBottom: 4 },
      h6: { fontSize: 18, fontWeight: 700, marginBottom: 2 },
    },
    pageTheme: brandedPageThemes,
    defaultPageTheme: 'home',
    components: withDefaults(uzumComponents(mode)),
  });
}

export const uzumTheme = createUzumTheme('light');
export const uzumDarkTheme = createUzumTheme('dark');
